/**
 * A sheet's secondary row indices: by label, by lookup key (multi-valued),
 * by formula name, by row class, and the header / footer surface anchors —
 * rebuilt lazily from the sheet's rows when a change marked them dirty.
 *
 * **Package-internal**: `Sheet` owns one and answers its reads through it
 * (`peekByLabel`, `peekByLookupKey`, `peekAllByLookupKey`, `findByType`,
 * `formulaReference`, `peekHeaderRow`, `peekFooterRow`). The rows, their
 * metadata, their formula-name entries and their surface anchors come
 * through `SheetRowIndex.Host`; it names no row type (the sheet instantiates
 * `Row`), so it sits outside the rows' type knot.
 */

import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { FormulaNameIndex } from "../formula/formula-name-index.ts";
import type { Network } from "../network.ts";

/** `dataMirrorsOf`'s answer for an owner no other row shows. */
const NO_MIRRORS: readonly RowLocalId[] = Object.freeze([]);

/** See the module note: the indices over a sheet's rows (`Row`, each with its `id`). */
export class SheetRowIndex<Row extends { readonly id: RowLocalId }> {
  readonly #host: SheetRowIndex.Host<Row>;
  #dirty = true;
  /**
   * `findByType`'s per-class index: the rows of each class asked for, in
   * the host's row order, kept until the row set changes (`rowsChanged`).
   */
  readonly #byType = new Map<SheetRowIndex.Ctor<Row>, readonly Row[]>();
  readonly #labelToId = new Map<string, RowLocalId>();
  /** Formula name candidates, separate from presentation's one-row lookup. */
  readonly #formulaNames = new FormulaNameIndex<Row>();
  // Multi-valued: several rows in one sheet can share a lookupKey when they
  // are distinct views of the same shared source (e.g. two weather rows over
  // `:~weather:{lid}`). `peekByLookupKey` returns any one; `peekAllByLookupKey`
  // returns all — used to fan a canonical-cell change out to every view.
  readonly #lookupKeyToId = new Map<string, Set<RowLocalId>>();
  // Surface-anchor caches (Phase A). The header / footer surface rows
  // are unique top-level inert rows of `kind: "header"` or
  // `kind: "sheet-footer"`. Re-derived alongside label / lookupKey
  // indices in `rebuildIfDirty`. `null` is the cached "no
  // surface anchor present" answer.
  #headerRowId: Option<RowLocalId> = null;
  #footerRowId: Option<RowLocalId> = null;
  /**
   * `dataMirrorsOf`'s index: data owner -> the other rows showing its cells.
   * Built on first read; dropped with the others (`invalidate`: a lookup key
   * moved) and when the row set changes (`rowsChanged`).
   */
  #dataMirrors: Option<Map<RowLocalId, readonly RowLocalId[]>> = null;

  constructor(host: SheetRowIndex.Host<Row>) {
    this.#host = host;
  }

  /** A label, lookup key, formula input or the tree's roots changed: rebuild on the next read. */
  invalidate(): void {
    this.#dirty = true;
    this.#dataMirrors = null;
  }

  /** The row set changed (a row added or removed): drop the per-class index. */
  rowsChanged(): void {
    this.#byType.clear();
    this.#dataMirrors = null;
  }

  /**
   * The rows other than `owner` whose cells are `owner`'s (`Host.dataOwner`:
   * aliases and projections of a source), in the host's row order; `owner`
   * need not be a row of this sheet (a canonical owner held elsewhere).
   */
  dataMirrorsOf(owner: RowLocalId): readonly RowLocalId[] {
    let index = this.#dataMirrors;
    if (index == null) {
      const built = new Map<RowLocalId, RowLocalId[]>();
      for (const row of this.#host.rows()) {
        const of = this.#host.dataOwner(row);
        if (of == null || of === row.id) continue;
        let list = built.get(of);
        if (list == null) built.set(of, list = []);
        list.push(row.id);
      }
      for (const list of built.values()) Object.freeze(list);
      this.#dataMirrors = index = built;
    }
    return index.get(owner) ?? NO_MIRRORS;
  }

  /** The formula-name candidates of `name` (lookup keys before labels; see `FormulaNameIndex`). */
  formulaCandidates(name: string): readonly Row[] {
    this.#rebuildIfDirty();
    return this.#formulaNames.candidates(name);
  }

  /** The row labelled `label`, `null` when none. */
  peekByLabel(label: string): Option<Row> {
    this.#rebuildIfDirty();
    const id = this.#labelToId.get(label);
    return id ? this.#host.row(id) : null;
  }

  /** The LAST row (in the host's row order) carrying `key`, `null` when none (see `Sheet.peekByLookupKey`). */
  peekByLookupKey(key: string): Option<Row> {
    this.#rebuildIfDirty();
    const ids = this.#lookupKeyToId.get(key);
    if (ids === undefined) return null;
    // Return the LAST member — the set preserves `rowsById` insertion order,
    // so this matches the previous single-valued `Map.set` last-wins behavior
    // that callers (provider-source resolution, formula, dynamicTail) relied
    // on. `peekAllByLookupKey` is the multi-row path for the fan-out.
    let last: Option<Row> = null;
    for (const id of ids) {
      const row = this.#host.row(id);
      if (row) last = row;
    }
    return last;
  }

  /** Every row carrying `key`. */
  peekAllByLookupKey(key: string): Row[] {
    this.#rebuildIfDirty();
    const ids = this.#lookupKeyToId.get(key);
    if (ids === undefined) return [];
    const rows: Row[] = [];
    for (const id of ids) {
      const row = this.#host.row(id);
      if (row) rows.push(row);
    }
    return rows;
  }

  /** Every row whose class (or superclass) is `ctor`, in the host's row order. */
  findByType<T extends Row>(ctor: SheetRowIndex.Ctor<T>): readonly T[] {
    let rows = this.#byType.get(ctor);
    if (rows === undefined) {
      rows = [...this.#host.rows()].filter((row) => row instanceof ctor);
      this.#byType.set(ctor, rows);
    }
    return rows.filter((row): row is T => row instanceof ctor);
  }

  /** The unique top-level header surface row, `null` when none (server > authored > legacy). */
  peekHeaderRow(): Option<Row> {
    this.#rebuildIfDirty();
    return this.#headerRowId === null || this.#headerRowId === undefined
      ? null
      : this.#host.row(this.#headerRowId);
  }

  /** The unique top-level footer surface row, `null` when none. */
  peekFooterRow(): Option<Row> {
    this.#rebuildIfDirty();
    return this.#footerRowId === null || this.#footerRowId === undefined
      ? null
      : this.#host.row(this.#footerRowId);
  }

  #rebuildIfDirty(): void {
    if (!this.#dirty) return;
    this.#labelToId.clear();
    this.#lookupKeyToId.clear();
    const formulaInputs: SheetRowIndex.FormulaInput<Row>[] = [];
    for (const row of this.#host.rows()) {
      const meta = this.#host.meta(row.id);
      if (meta.label != null) this.#labelToId.set(meta.label, row.id);
      formulaInputs.push(this.#host.formulaInput(row, meta));
      if (meta.lookupKey != null) {
        const set = this.#lookupKeyToId.get(meta.lookupKey);
        if (set === undefined) {
          this.#lookupKeyToId.set(meta.lookupKey, new Set([row.id]));
        } else {
          set.add(row.id);
        }
      }
    }
    this.#formulaNames.replace(formulaInputs);
    // Surface-anchor lookup. `SurfaceAnchor.of` recognizes three
    // shapes per kind (see ../render/surface-anchor.ts):
    //   1. `server` — server-backed surface container (`InertRow` of
    //      kind "surface-container" + `RowMetadata.meta.surface`).
    //   2. `authored` — template/user-authored surface region
    //      (`InertRow` of kind "surface" +
    //      `attributes.surface = "header" | "footer"`).
    //   3. `legacy` — pre-Phase-D synthetic placeholder (`InertRow`
    //      of kind "header" / "sheet-footer" at top level directly).
    // Tie-break per kind: server > authored > legacy; the losers are
    // suppressed for that surface.
    let headerServer: Option<RowLocalId> = null;
    let headerAuthored: Option<RowLocalId> = null;
    let headerLegacy: Option<RowLocalId> = null;
    let footerServer: Option<RowLocalId> = null;
    let footerAuthored: Option<RowLocalId> = null;
    let footerLegacy: Option<RowLocalId> = null;
    for (const id of this.#host.roots()) {
      const row = this.#host.row(id);
      if (!row) continue;
      const anchor = this.#host.anchor(row);
      if (!anchor) continue;
      if (anchor.kind === "header") {
        if (anchor.source === "server") headerServer ??= id;
        else if (anchor.source === "authored") headerAuthored ??= id;
        else headerLegacy ??= id;
      } else {
        if (anchor.source === "server") footerServer ??= id;
        else if (anchor.source === "authored") footerAuthored ??= id;
        else footerLegacy ??= id;
      }
    }
    this.#headerRowId = headerServer ?? headerAuthored ?? headerLegacy;
    this.#footerRowId = footerServer ?? footerAuthored ?? footerLegacy;
    this.#dirty = false;
  }
}

/** Types that go with `SheetRowIndex`: `Host`, `Ctor`, `Meta`, `FormulaInput`, `Anchor`. */
export namespace SheetRowIndex {
  /** What the indices read of their sheet. */
  export interface Host<Row> {
    /** Every row of the sheet, in its by-id order. */
    rows(): Iterable<Row>;
    /** The row `id` names, `null` when it is not in the sheet. */
    row(id: RowLocalId): Option<Row>;
    /** The tree's root ids, in tree order. */
    roots(): readonly RowLocalId[];
    /** The row's label and lookup key (its metadata). */
    meta(id: RowLocalId): Meta;
    /** The row's formula-name entry (the cell-bearing row it names, and how). */
    formulaInput(row: Row, meta: Meta): FormulaInput<Row>;
    /** The row's surface anchor shape, `null` when it is not one (`SurfaceAnchor.of`). */
    anchor(row: Row): Option<Anchor>;
    /** The row whose cells `row` shows (itself for a source), `null` when it shows none. */
    dataOwner(row: Row): Option<RowLocalId>;
  }

  /** A row class, as `findByType` takes it. */
  export type Ctor<T> = new (...args: never[]) => T;

  /** A row's indexed metadata. */
  export type Meta = { readonly label: Option<string>; readonly lookupKey: Option<string> };

  /** One row's entry in the formula-name index (`FormulaNameIndex.replace`). */
  export type FormulaInput<Row> = Parameters<FormulaNameIndex<Row>["replace"]>[0][number];

  /** A surface anchor's kind and where it came from (`SurfaceAnchor.Anchor`). */
  export type Anchor = {
    readonly kind: Network.SurfaceKind;
    readonly source: "server" | "authored" | "legacy";
  };
}
